import React from 'react';
import { View, Text } from 'react-native';
import Svg, { Rect, Text as SvgText, Line } from 'react-native-svg';
import { ChartConfig } from '../types';

interface BarChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      data: number[];
      color?: any;
    }>;
  };
  width: number;
  height: number;
  chartConfig: ChartConfig;
  showGrid?: boolean;
  showValuesOnTopOfBars?: boolean;
  onDataPointClick?: (data: any, index: number) => void;
  isZeroValues?: boolean;
  style?: any;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  width,
  height,
  chartConfig,
  showGrid = true,
  showValuesOnTopOfBars = false,
  onDataPointClick,
  isZeroValues = false,
  style,
}) => {
  // Validation: Check if data exists and has proper structure
  if (
    !data ||
    !data.datasets ||
    !data.datasets[0] ||
    !data.datasets[0].data ||
    data.datasets[0].data.length === 0
  ) {
    console.warn('BarChart: Invalid data structure provided');
    return (
      <View
        style={[
          {
            backgroundColor: chartConfig.backgroundColor,
            justifyContent: 'center',
            alignItems: 'center',
          },
          style,
        ]}
      >
        <Text>No data available</Text>
      </View>
    );
  }

  const paddingTop = 20;
  const paddingBottom = 40;
  const paddingHorizontal = 20;
  const chartWidth = width - paddingHorizontal * 2;
  const chartHeight = height - paddingTop - paddingBottom;

  const dataset = data.datasets[0];
  const chartData = dataset.data;
  const labels =
    data.labels || chartData.map((_, index) => `Item ${index + 1}`);

  // Ensure we have valid numeric data
  const validData = chartData.map((val) => (typeof val === 'number' ? val : 0));
  const maxValue = Math.max(...validData, 1); // Ensure maxValue is at least 1

  const barWidth = (chartWidth / labels.length) * 0.7;
  const barSpacing = (chartWidth / labels.length) * 0.3;

  return (
    <View style={[{ backgroundColor: chartConfig.backgroundColor }, style]}>
      <Svg width={width} height={height}>
        {/* Grid lines */}
        {showGrid && (
          <>
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => (
              <Line
                key={index}
                x1={paddingHorizontal}
                y1={paddingTop + chartHeight * ratio}
                x2={width - paddingHorizontal}
                y2={paddingTop + chartHeight * ratio}
                stroke={
                  chartConfig.propsForBackgroundLines?.stroke || '#e3e3e3'
                }
                strokeWidth={
                  chartConfig.propsForBackgroundLines?.strokeWidth || 1
                }
              />
            ))}
          </>
        )}

        {/* Bars */}
        {validData.map((value, index) => {
          const barHeight = isZeroValues
            ? 5
            : Math.max((value / maxValue) * chartHeight, 1);
          const x =
            paddingHorizontal +
            index * (barWidth + barSpacing) +
            barSpacing / 2;
          const y = paddingTop + chartHeight - barHeight;

          return (
            <React.Fragment key={index}>
              <Rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={dataset.color || chartConfig.color || '#0175ff'}
                onPress={() => onDataPointClick?.({ value, index }, index)}
              />
              {showValuesOnTopOfBars && (
                <SvgText
                  x={x + barWidth / 2}
                  y={y - 5}
                  textAnchor="middle"
                  fontSize="12"
                  fill={chartConfig.color?.(1) || '#333'}
                >
                  {value}
                </SvgText>
              )}
              <SvgText
                x={x + barWidth / 2}
                y={height - 15}
                textAnchor="middle"
                fontSize="12"
                fill={chartConfig.color?.(1) || '#333'}
              >
                {labels[index] || `Item ${index + 1}`}
              </SvgText>
            </React.Fragment>
          );
        })}
      </Svg>
    </View>
  );
};

export default BarChart;
